import { Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

const RedirectInsightToNews = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/news/${slug}`} replace />;
};
import { AuthProvider } from "./components/AuthProvider";
import { LayoutProvider } from "./contexts/LayoutContext";
import { ExchangeRatesProvider } from "./contexts/ExchangeRatesContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";
import { TutorialProvider } from "./components/tutorial/TutorialProvider";
import AppLayout from "./components/AppLayout";
import UnifiedLoader from "./components/ui/UnifiedLoader";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import { PWAUpdatePrompt } from "./components/pwa/PWAUpdatePrompt";
import { ScrollToTop } from "./components/ScrollToTop";
import AnalyticsTracker from "./components/AnalyticsTracker";

import { lazyWithRetry, clearRetryFlag } from "./lib/lazyWithRetry";

// Eagerly load critical pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import EnhancedMarketplace from "./components/EnhancedMarketplace";

// Lazy load heavy pages with retry logic for chunk failures
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AdminConsole = lazyWithRetry(() => import("./pages/AdminConsole"));
const ContentReview = lazyWithRetry(() => import("./pages/admin/ContentReview"));
const CreateListing = lazyWithRetry(() => import("./pages/CreateListing"));
const CreateEngineListing = lazyWithRetry(() => import("./pages/CreateEngineListing"));
const CreatePartsListing = lazyWithRetry(() => import("./pages/CreatePartsListing"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const IconUpload = lazyWithRetry(() => import("./pages/IconUpload"));
const MobileCreate = lazyWithRetry(() => import("./pages/MobileCreate"));
const MobileAssetSelector = lazyWithRetry(() => import("./pages/MobileAssetSelector"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazyWithRetry(() => import("./pages/PaymentCanceled"));
const AdminLocationFix = lazyWithRetry(() => import("./pages/AdminLocationFix"));
const AdminValuation = lazyWithRetry(() => import("./pages/admin/AdminValuation"));
const VisitCard = lazyWithRetry(() => import("./pages/VisitCard"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const Competitors = lazyWithRetry(() => import("./pages/Competitors"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const FAQPage = lazyWithRetry(() => import("./pages/FAQ"));
const WhyChopperSwap = lazyWithRetry(() => import("./pages/WhyChopperSwap"));
const HowToTutorials = lazyWithRetry(() => import("./pages/HowToTutorials"));
const News = lazyWithRetry(() => import("./pages/News"));
const NewsArticle = lazyWithRetry(() => import("./pages/NewsArticle"));
const ShareListing = lazyWithRetry(() => import("./pages/ShareListing"));
const Partners = lazyWithRetry(() => import("./pages/Partners"));
const RenderListingCard = lazyWithRetry(() => import("./pages/RenderListingCard"));
const UserListings = lazyWithRetry(() => import("./pages/UserListings"));
const NewsletterPreferences = lazyWithRetry(() => import("./pages/NewsletterPreferences"));
const CreateJobsListing = lazyWithRetry(() => import("./pages/CreateJobsListing"));
const CreateServicesListing = lazyWithRetry(() => import("./pages/CreateServicesListing"));
const ServicesMarketplace = lazyWithRetry(() => import("./pages/ServicesMarketplace"));
const RenewListing = lazyWithRetry(() => import("./pages/RenewListing"));
const ProSubscription = lazyWithRetry(() => import("./pages/ProSubscription"));
const HelicopterListingPage = lazyWithRetry(() => import("./pages/HelicopterListingPage"));
// SEO category landing pages — purely additive, do not modify existing flows
const CategoryLanding = lazyWithRetry(() => import("./pages/seo/CategoryLanding"));
const HubLanding = lazyWithRetry(() => import("./pages/seo/HubLanding"));
const Directory = lazyWithRetry(() => import("./pages/seo/Directory"));
const HowToLanding = lazyWithRetry(() => import("./pages/seo/HowToLanding"));
const HowToIndex = lazyWithRetry(() => import("./pages/seo/HowToIndex"));

// Loading fallback for route transitions
// Returns null to avoid "flash" - chunks typically load in 50-200ms
// For slow networks, the previous page remains visible (better UX than a flash)
const PageLoader = () => null;

// Configure QueryClient with optimized caching for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 30 * 60 * 1000, // Cache persists for 30 minutes (extended for better reuse)
      refetchOnWindowFocus: false, // Prevent unnecessary refetches on tab switch
      refetchOnMount: true, // Only refetch on mount if data is stale (was 'always')

      retry: 1, // Retry failed queries once
      networkMode: 'offlineFirst', // Use cached data when offline
    },
  },
});

const App = () => {
  // Cancel boot watchdog and clear retry flag on successful mount
  useEffect(() => {
    // Signal successful mount to watchdog
    (window as unknown as { __appMounted: boolean }).__appMounted = true;
    
    const bootTime = performance.now();
    console.log(`[BOOT] App mounted in ${Math.round(bootTime)}ms`);
    
    // Cancel all watchdog timers
    (window as unknown as { __cancelBootWatchdog?: () => void }).__cancelBootWatchdog?.();
    
    // Remove any failure overlay that may have appeared during slow boot
    (window as unknown as { __clearBootFailureOverlay?: () => void }).__clearBootFailureOverlay?.();
    
    // Remove boot skeleton now that React has mounted
    const skeleton = document.getElementById('boot-skeleton');
    if (skeleton) skeleton.remove();
    
    console.log('[BOOT] Watchdog + overlay cleanup complete');
    
    // Clear chunk retry flag on successful mount
    clearRetryFlag();
  }, []);

  return (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserPreferencesProvider>
        <LayoutProvider>
          <CurrencyProvider>
            <ExchangeRatesProvider>
              <TooltipProvider>
              <Toaster />
              <PWAUpdatePrompt />
              <BrowserRouter>
                <ScrollToTop />
                <AnalyticsTracker />
                <TutorialProvider>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/engines" element={<Index />} />
                        <Route path="/parts-tools" element={<Index />} />
                        <Route path="/jobs" element={<Index />} />
                        <Route path="/services" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                        <Route path="/marketplace" element={<EnhancedMarketplace />} />
                        <Route path="/create-listing" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
                        <Route path="/mobile-create" element={<ProtectedRoute><MobileCreate /></ProtectedRoute>} />
                        <Route path="/mobile-asset-selector" element={<ProtectedRoute><MobileAssetSelector /></ProtectedRoute>} />
                        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                        <Route path="/payment-canceled" element={<ProtectedRoute><PaymentCanceled /></ProtectedRoute>} />
                        <Route path="/icon-upload" element={<AdminRoute><IconUpload /></AdminRoute>} />
                        <Route path="/admin" element={<AdminRoute><AdminConsole /></AdminRoute>} />
                        <Route path="/admin/content-review" element={<AdminRoute><ContentReview /></AdminRoute>} />
                        <Route path="/admin/fix-locations" element={<AdminRoute><AdminLocationFix /></AdminRoute>} />
                        <Route path="/admin/valuation" element={<AdminRoute><AdminValuation /></AdminRoute>} />
                        <Route path="/visitcard" element={<AdminRoute><VisitCard /></AdminRoute>} />
                        <Route path="/create-listing-engine" element={<ProtectedRoute><CreateEngineListing /></ProtectedRoute>} />
                        <Route path="/create-listing-parts" element={<ProtectedRoute><CreatePartsListing /></ProtectedRoute>} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/cookies" element={<CookiePolicy />} />
                        <Route path="/competitors" element={<Competitors />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/why-chopperswap" element={<WhyChopperSwap />} />
                        <Route path="/tutorials" element={<HowToTutorials />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/news/:slug" element={<NewsArticle />} />
                        <Route path="/share/:assetType/:listingId" element={<ShareListing />} />
                        <Route path="/partners" element={<Partners />} />
                        <Route path="/render-listing/:assetType/:listingId" element={<RenderListingCard />} />
                        <Route path="/user/:userId" element={<UserListings />} />
                        <Route path="/company/:slug" element={<UserListings />} />
                        <Route path="/newsletter/preferences" element={<NewsletterPreferences />} />
                        <Route path="/create-listing-jobs" element={<ProtectedRoute><CreateJobsListing /></ProtectedRoute>} />
                        <Route path="/create-listing-services" element={<ProtectedRoute><CreateServicesListing /></ProtectedRoute>} />
                        <Route path="/services/marketplace" element={<ServicesMarketplace />} />
                        <Route path="/listing/:listingId/renew" element={<ProtectedRoute><RenewListing /></ProtectedRoute>} />
                        <Route path="/pro" element={<ProSubscription />} />
                        <Route path="/helicopter/:slug" element={<HelicopterListingPage />} />
                        {/* SEO category landing pages — Google entry doors, additive only */}
                        <Route path="/helicopters-for-sale" element={<CategoryLanding />} />
                        <Route path="/helicopter-parts-for-sale" element={<CategoryLanding />} />
                        <Route path="/turbine-engines-for-sale" element={<CategoryLanding />} />
                        <Route path="/helicopter-pilot-jobs" element={<CategoryLanding />} />
                        <Route path="/helicopter-maintenance-services" element={<CategoryLanding />} />
                        {/* SEO programmatic hubs (Week 3) — manufacturer / model / location / category */}
                        <Route path="/helicopters/:manufacturer" element={<HubLanding />} />
                        <Route path="/helicopters/:manufacturer/:model" element={<HubLanding />} />
                        <Route path="/helicopters-in/:country" element={<HubLanding />} />
                        <Route path="/aircraft-engines/:manufacturer" element={<HubLanding />} />
                        <Route path="/parts/:manufacturer" element={<HubLanding />} />
                        <Route path="/aviation-jobs/:category" element={<HubLanding />} />
                        <Route path="/aviation-jobs/:role/:location" element={<HubLanding />} />
                        <Route path="/aviation-services/:category" element={<HubLanding />} />
                        <Route path="/directory" element={<Directory />} />
                        <Route path="/how-to" element={<HowToIndex />} />
                        <Route path="/how-to/:slug" element={<HowToLanding />} />
                        {/* /insights merged into /news (consolidated content hub) */}
                        <Route path="/insights" element={<Navigate to="/news?tab=insights" replace />} />
                        <Route path="/insights/:slug" element={<RedirectInsightToNews />} />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </AppLayout>
                  
                </TutorialProvider>
              </BrowserRouter>
              </TooltipProvider>
            </ExchangeRatesProvider>
          </CurrencyProvider>
        </LayoutProvider>
        </UserPreferencesProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
  );
};

export default App;
